import { useCallback, useEffect, useState, useRef } from 'react';
import anime from 'animejs';
import { throttle } from 'utils';

export const useIsSsr = () => {
  // we always start off in "SSR mode", to ensure our initial browser render
  // matches the SSR render
  const [isSsr, setIsSsr] = useState(true);

  useEffect(() => {
    // `useEffect` never runs on the server, so we must be on the client if
    // we hit this block
    setIsSsr(false);
  }, []);

  return isSsr;
};
/**
 * Управление состоянием компонента Tooltip
 * @param dependencies {array} - зависимости для хука useCallback (для оптимизации)
 * @returns {{isOpen: boolean, showTooltip: function, hideTooltip: function}}
 */
export const useTooltipState = (dependencies = []) => {
  const [isOpen, setIsOpen] = useState(false);

  const showTooltip = useCallback(() => setIsOpen(true), dependencies);
  const hideTooltip = useCallback(() => setIsOpen(false), dependencies);

  return { isOpen, showTooltip, hideTooltip };
};

export const useStateModal = () => {
  const [modalState, setModalState] = useState({ isOpen: false });

  const showModal = useCallback(() => setModalState({ isOpen: true }), []);
  const hideModal = useCallback(() => setModalState({ isOpen: false }), []);

  return { modalState, showModal, hideModal };
};

export const useStateModals = () => {
  const [modalState, setModalState] = useState({});

  const showModal = useCallback(key => setModalState({ ...modalState, [key]: true }), []);
  const hideModal = useCallback(key => () => setModalState({ ...modalState, [key]: false }), []);

  return { modalState, showModal, hideModal };
};

export function useLoadingComponent(ApiCall, ...args) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    ApiCall(...args)
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  return { data, loading };
}

export function useDocumentScrollThrottled(callback) {
  const [, setScrollPosition] = useState(0);
  let previousScrollTop = 0;

  function handleDocumentScroll() {
    const { scrollTop: currentScrollTop } = document.documentElement || document.body;

    setScrollPosition(previousPosition => {
      previousScrollTop = previousPosition;
      return currentScrollTop;
    });

    callback({ previousScrollTop, currentScrollTop });
  }

  const handleDocumentScrollThrottled = throttle(handleDocumentScroll, 250);

  useEffect(() => {
    window.addEventListener('scroll', handleDocumentScrollThrottled);

    return () => window.removeEventListener('scroll', handleDocumentScrollThrottled);
  }, []);
}

export const useClickOutside = (refs, handler) => {
  useEffect(() => {
    const targets = refs.map(el => el.current);

    const listener = e => {
      if (
        !targets.some(el => el?.contains && el.contains(e.target)) &&
        typeof handler === 'function'
      ) {
        handler();
      }
    };

    document.addEventListener('mouseup', listener);
    document.addEventListener('touchend', listener);
    return () => {
      document.removeEventListener('mouseup', listener);
      document.removeEventListener('touchend', listener);
    };
  }, [refs, handler]);
};

export const useLazyEffect = (fn, deps) => {
  const isRendered = useRef(false);

  useEffect(() => {
    if (isRendered.current) {
      fn();
    } else {
      isRendered.current = true;
    }
  }, deps);
};

export const useWindowResize = () => {
  const [width, setWidth] = useState(null);

  useEffect(() => {
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { width };
};

export function useHorizontalScroll(deps) {
  const scrollRef = useRef();
  const [scrollLeft, setScrollLeft] = useState(null);

  useEffect(() => {
    const el = scrollRef.current;

    if (el) {
      const onWheel = e => {
        const scrollIsNotNeeded = el.clientWidth === el.scrollWidth;
        setScrollLeft(el.scrollLeft);

        if (e.deltaY === 0) return;
        if (scrollIsNotNeeded) return;

        e.preventDefault();

        el.scrollTo({
          left: el.scrollLeft + e.deltaY,
        });
      };

      el.addEventListener('wheel', onWheel);

      return () => {
        el.removeEventListener('wheel', onWheel);
      };
    }
  }, [deps]);

  return { scrollRef, scrollLeft, setScrollLeft };
}

export const useDropDown = (headRef, dropDownRef, arrowUpRef, arrowDownRef, onClose) => {
  const animationDuration = 200;
  const animationElasticDuration = animationDuration * 3;
  const animationEasing = 'easeOutCubic';

  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const isVisible = useRef(false);

  useEffect(() => {
    const dropdown = dropDownRef.current;

    dropdown.style.opacity = 0;
    dropdown.style.zIndex = -1;
    dropdown.style.transform = 'scale(0.98)';
    dropdown.style.pointerEvents = 'none';
  }, []);

  useLazyEffect(() => {
    if (isDropDownOpen) {
      open();
    } else {
      close();
    }
  }, [isDropDownOpen]);

  useClickOutside([headRef, dropDownRef], () => setIsDropDownOpen(false));

  const open = () => {
    const dropdown = dropDownRef.current;

    isVisible.current = true;
    anime.remove([arrowUpRef.current, arrowDownRef.current, dropDownRef.current]);

    anime({
      targets: arrowDownRef.current,
      translateY: [0, 5],
      opacity: [1, 0],
      duration: animationDuration,
      easing: animationEasing,
      complete: () => {
        anime({
          targets: arrowUpRef.current,
          translateY: [5, 0],
          rotateZ: {
            value: '180deg',
            duration: 0,
          },
          opacity: [0, 1],
          duration: animationDuration,
          easing: animationEasing,
        });
      },
    });

    anime({
      targets: dropDownRef.current,
      opacity: 1,
      scale: {
        value: 1,
        duration: animationElasticDuration,
        easing: 'easeOutElastic(4, .5)',
      },
      duration: animationDuration,
      easing: animationEasing,
      begin: () => {
        dropdown.style.zIndex = 999;
        dropdown.style.pointerEvents = 'auto';
      },
    });
  };

  const close = () => {
    const dropdown = dropDownRef.current;

    anime.remove([arrowUpRef.current, arrowDownRef.current, dropDownRef.current]);

    anime({
      targets: arrowUpRef.current,
      translateY: [0, -5],
      opacity: [1, 0],
      duration: animationDuration,
      easing: animationEasing,
      complete: () => {
        anime({
          targets: arrowDownRef.current,
          translateY: [-5, 0],
          opacity: [0, 1],
          duration: animationDuration,
          easing: animationEasing,
          complete: () => {
            if (typeof onClose === 'function') {
              onClose();
            }
          },
        });
      },
    });

    anime({
      targets: dropDownRef.current,
      opacity: 0,
      duration: animationDuration,
      easing: animationEasing,
      scale: 0.98,
      complete: () => {
        dropdown.style.zIndex = -1;
        dropdown.style.pointerEvents = 'none';
        isVisible.current = false;
      },
    });
  };

  return {
    open: () => setIsDropDownOpen(true),
    close: () => setIsDropDownOpen(false),
    toggle: () => {
      setIsDropDownOpen(!isDropDownOpen);
      return !isDropDownOpen;
    },
    isOpen: isDropDownOpen,
  };
};

export function useDeviceType() {
  const [state, setState] = useState({ deviceType: null });

  const changeWindowHandler = useCallback(event => {
    setState(prev => ({
      ...prev,
      deviceType: event.matches ? 'desktop' : 'mobile',
    }));
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 657px)');

    const windowResizeHandler = () => {
      setState(prev => ({
        ...prev,
        deviceType: window.innerWidth >= 657 ? 'desktop' : 'mobile',
      }));
    };

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', changeWindowHandler);
      setState(prev => ({
        ...prev,
        deviceType: mediaQuery.matches ? 'desktop' : 'mobile',
      }));
    } else {
      window.addEventListener('resize', windowResizeHandler);
      windowResizeHandler();
    }

    return () => {
      if (mediaQuery.addEventListener) {
        mediaQuery.removeEventListener('change', changeWindowHandler);
      } else {
        window.removeEventListener('resize', windowResizeHandler);
      }
    };
  }, []);

  return state;
}
